import { useEffect, useState, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../../api/api";
import "./Dashboard.css";
import StreakSettingsModal from "../../components/Streak/StreakSettingsModal";
import InfoModal from "../../components/InfoModal/InfoModal";
import { FaGear } from "react-icons/fa6";
import ProductivityModal from "../../components/Productivity/ProductivityModal";
import ProductivityCard from "../../components/Productivity/ProductivityCard";
import RoadmapCard from "../../components/Roadmap/RoadmapCard";
import FocusChart from "../../components/Analytics/FocusChart";
import HeatmapCard from "../../components/Analytics/HeatmapCard";
import TodoCard from "../../components/Todo/TodoCard";
import { AuthContext } from "../../context/AuthContext";
import StreakFreezeModal from "../../components/StreakFreezeModal/StreakFreezeModal";
import CalendarBanner from "../../components/Dashboard/CalendarBanner";
import ProBadge from "../../components/ProBadge/ProBadge";
import TrackProgressCard from "../../components/Dashboard/TrackProgressCard";
import DailyTargetWidget from "../../components/Dashboard/DailyTargetWidget";
import ProAnalyticsSection from "../../components/Dashboard/ProAnalyticsSection";
import ProductTour from "../../components/ProductTour/ProductTour";
import FoundingBanner from "../../components/Promo/FoundingBanner";

/* ── helpers ──────────────────────────────────────────────── */
const daysSince = (dateStr) => {
  if (!dateStr) return null;
  try {
    const diff = Date.now() - new Date(dateStr).getTime();
    const result = Math.floor(diff / (1000 * 60 * 60 * 24));
    return isNaN(result) ? null : result;
  } catch {
    return null;
  }
};

const safePercent = (value, total) => {
  if (!total || total === 0 || !value) return 0;
  return Math.min(Math.max((value / total) * 100, 0), 100);
};

function Dashboard() {
  const { user, checkAuth } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  /* ── state ───────────────────────────────────────────────── */
  const [courses, setCourses] = useState([]);
  const [resume, setResume] = useState(null);
  const [streak, setStreak] = useState(null);
  const [runTour, setRunTour] = useState(false);

  const [showStreakModal, setShowStreakModal] = useState(false);
  const [showStreakInfo, setShowStreakInfo] = useState(false);

  // roadmap
  const [roadmap, setRoadmap] = useState(null);
  const [roadmapCount, setRoadmapCount] = useState(0);
  const [roadmapProgress, setRoadmapProgress] = useState(null);
  const [currentStep, setCurrentStep] = useState(null);
  const [upcomingItems, setUpcomingItems] = useState([]);

  // charts
  const [chartData, setChartData] = useState([]);
  const [days, setDays] = useState(7);
  const [heatmapData, setHeatmapData] = useState([]);
  const [consistencyScore, setConsistencyScore] = useState(0);
  const [heatmapDays, setHeatmapDays] = useState(90);

  // productivity
  const [showProductivityModal, setShowProductivityModal] = useState(false);

  // streak freeze
  const [missedDay, setMissedDay] = useState(false);
  const [showStreakFreezeModal, setShowStreakFreezeModal] = useState(false);
  const [freezeCredits, setFreezeCredits] = useState(0);

  // delete course
  const [showDeleteCourseModal, setShowDeleteCourseModal] = useState(false);
  const [courseToDelete, setCourseToDelete] = useState(null);
  const [deleteText, setDeleteText] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);

  /* ── fetches ─────────────────────────────────────────────── */
  useEffect(() => {
    checkStreakStatus();
    fetchCourses();
    fetchStreak();
    fetchRoadmap();
    fetchProductivityStatus();
    fetchChartData(days);
    fetchHeatmap(heatmapDays);
    // Auto-start tour for new users
    if (!localStorage.getItem("learnlia_tour_completed")) {
      localStorage.setItem("learnlia_tour_completed", "true");
      setTimeout(() => setRunTour(true), 1500); // Wait for animations to finish
    }
    // eslint-disable-next-line
  }, [location.pathname, days, heatmapDays]);

  const fetchChartData = async (selectedDays = days) => {
    try {
      const res = await api.get(`/analytics/focus-trend?days=${selectedDays}`);
      setChartData(Array.isArray(res.data) ? res.data : []);
    } catch {
      setChartData([]);
    }
  };

  const fetchHeatmap = async (d = heatmapDays) => {
    try {
      const res = await api.get(`/analytics/heatmap?days=${d}`);
      setHeatmapData(Array.isArray(res.data?.heatmap) ? res.data.heatmap : []);
      setConsistencyScore(res.data?.consistencyScore ?? 0);
    } catch {
      setHeatmapData([]);
      setConsistencyScore(0);
    }
  };

  const checkStreakStatus = async () => {
    try {
      const res = await api.get("/streak/status");
      if (res.data?.status === "missed") {
        setMissedDay(true);
        setFreezeCredits(res.data?.freezeCredits ?? 0);
        setShowStreakFreezeModal(true);
      }
    } catch { }
  };

  const fetchCourses = async () => {
    try {
      const res = await api.get("/courses/mine");
      const valid = (Array.isArray(res.data) ? res.data : []).filter(
        (enrollment) => enrollment?.course?._id
      );
      setCourses(valid);
      if (valid.length > 0) {
        const sorted = [...valid].sort(
          (a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0)
        );
        if (sorted[0]?.course?._id) fetchResume(sorted[0].course._id);
      }
    } catch {
      setCourses([]);
    }
  };

  const fetchProductivityStatus = async () => {
    try {
      await api.get("/productivity/today");
    } catch { }
  };

  const fetchResume = async (courseId) => {
    if (!courseId) { setResume(null); return; }
    try {
      const res = await api.get(`/progress/continue/${courseId}`);
      setResume(res.data?.courseId ? res.data : null);
    } catch {
      setResume(null);
    }
  };

  const fetchStreak = async () => {
    try {
      const res = await api.get("/streak");
      setStreak(res.data ?? null);
    } catch {
      setStreak(null);
    }
  };

  const fetchRoadmap = async () => {
    try {
      const res = await api.get("/roadmap/active");
      setRoadmap(res.data?.roadmap ?? null);
      setRoadmapCount(res.data?.activeCount ?? 0);
      setRoadmapProgress(res.data?.progress ?? null);
      setCurrentStep(res.data?.currentStep ?? null);
      setUpcomingItems(Array.isArray(res.data?.upcomingItems) ? res.data.upcomingItems : []);
    } catch {
      setRoadmap(null);
      setRoadmapProgress(null);
      setCurrentStep(null);
      setUpcomingItems([]);
    }
  };

  /* ── continue roadmap ───────────────────────────────────── */
  const handleContinueRoadmap = async () => {
    if (!roadmap?._id) return;
    if (currentStep?.type === "course" && currentStep?.courseId) {
      navigate(`/course/${currentStep.courseId}`, { state: { from: "roadmap" } });
      return;
    }
    if (currentStep?.type === "project" && currentStep?.projectId) {
      navigate(`/project/${roadmap._id}`);
      return;
    }
    try {
      const res = await api.get(`/roadmap/current-course/${roadmap._id}`);
      if (res.data?.type === "course" && res.data?.courseId)
        navigate(`/course/${res.data.courseId}`, { state: { from: "roadmap" } });
      if (res.data?.type === "project" && res.data?.projectId)
        navigate(`/project/${roadmap._id}`);
    } catch {
      navigate(`/roadmap/${roadmap._id}`);
    }
  };

  /* ── delete course ──────────────────────────────────────── */
  const handleDeleteCourse = async () => {
    if (!courseToDelete?._id || deleteText !== "DELETE") return;
    setDeleteLoading(true);
    try {
      await api.delete(`/courses/${courseToDelete._id}`);
      setShowDeleteCourseModal(false);
      setDeleteText("");
      setCourseToDelete(null);
      fetchCourses();
    } catch {
    } finally {
      setDeleteLoading(false);
    }
  };

  /* ── derived values ─────────────────────────────────────── */
  const streakSecured = streak?.todayCompleted ?? false;
  const requiredMins = streak?.requiredMinutes || 30;
  const todayMins = streak?.todayMinutes || 0;
  const minsNeeded = Math.max(0, requiredMins - todayMins);
  const streakFillPct = safePercent(todayMins, requiredMins);

  const lastActivityDays = resume?.lastWatched ? daysSince(resume.lastWatched) : null;
  const showReengagement = !streakSecured && lastActivityDays !== null && lastActivityDays >= 3;
  const roadmapComplete = roadmap?.careerCompleted === true;

  /* ── render ──────────────────────────────────────────────── */
  return (
    <div className="dashboard">
      <div className="dashboard-container">

        {/* ── Header ──────────────────────────────────────────── */}
        <div className="dashboard-header">
          <div className="dashboard-header-flex">
            <div className="dashboard-greeting">
              <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                <h1>Welcome back, {user?.name ?? "there"} 👋</h1>
                <button 
                  onClick={() => setRunTour(true)}
                  style={{
                    padding: "2px 10px",
                    background: "var(--bg-card)",
                    border: "1px solid var(--accent, #2563eb)",
                    color: "var(--accent, #2563eb)",
                    borderRadius: "99px",
                    fontSize: "11px",
                    fontWeight: "600",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    transition: "all 0.2s"
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = "var(--accent, #2563eb)";
                    e.currentTarget.style.color = "#fff";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = "var(--bg-card)";
                    e.currentTarget.style.color = "var(--accent, #2563eb)";
                  }}
                >
                  🚀 Take a Tour
                </button>
              </div>
              {/* <p className="dashboard-subtitle">Here's your learning snapshot for today</p> */}
            </div>
            {user?.subscription?.status === "ACTIVE" && (
              <ProBadge size="hero" />
            )}
          </div>
        </div>

        <FoundingBanner />

        <CalendarBanner user={user} onSuccess={checkAuth} />

        {/* ── Banners ─────────────────────────────────────────── */}
        {showReengagement && (
          <div className="reengagement-banner">
            <span>
              😴 You haven't studied in {lastActivityDays} day{lastActivityDays !== 1 ? "s" : ""} — pick up where you left off!
            </span>
            {resume?.courseId && (
              <button onClick={() => navigate(`/course/${resume.courseId}`)}>Resume →</button>
            )}
          </div>
        )}

        {roadmapComplete && (
          <div className="roadmap-complete-banner">
            <span className="roadmap-complete-icon">🎉</span>
            <div>
              <strong>You completed your roadmap!</strong>
              <p>Every skill and project is done. Time to start a new journey.</p>
            </div>
            <button onClick={() => navigate("/roadmap/create")}>New Roadmap →</button>
          </div>
        )}

        <div className="insight-bar">
          <p>
            🎯 <strong>Learnlia helps you stay consistent.</strong>
            <span> Track your focus, maintain streaks, and turn watching into real progress.</span>
          </p>
        </div>

        <div className="streak-info-banner">
          🔥 Daily streak is primarily earned by watching course videos.
          Productivity log is for practical work reflection.
        </div>

        {/* ── Top Section ─────────────────────────────────────── */}
        <div className="top-section">

          {/* 🔥 Streak Card */}
          <div
            className={`stat-card streak-card ${missedDay ? "streak-warning" : ""}`}
            onClick={() => { if (missedDay) setShowStreakFreezeModal(true); }}
          >
            <div className="streak-top">
              <div className="stat-title">🔥 Streak</div>
              <button
                className="streak-settings-btn"
                onClick={(e) => { e.stopPropagation(); setShowStreakModal(true); }}
              >
                <FaGear className="gear-icon" />
              </button>
            </div>

            {missedDay && <div className="streak-warning-text">⚠ Resolve missed streak</div>}

            <div className="stat-value">{streak?.currentStreak ?? 0}</div>
            <div className="stat-sub">days</div>

            <div className="streak-progress-wrapper">
              <div className="streak-progress-bar">
                <div className="streak-progress-fill" style={{ width: `${streakFillPct}%` }} />
              </div>
              <span className="streak-progress-text">{todayMins} / {requiredMins} mins</span>
            </div>

            <div className={`streak-roadmap-hint ${streakSecured ? "streak-roadmap-hint--done" : ""}`}>
              {streakSecured
                ? "✅ Streak secured today!"
                : `Watch ${minsNeeded} more min${minsNeeded !== 1 ? "s" : ""} to secure streak`}
              {!streakSecured && roadmap && !roadmapComplete && currentStep?.type === "course" && currentStep?.courseId && (
                <button
                  className="streak-roadmap-link"
                  onClick={(e) => { e.stopPropagation(); navigate(`/course/${currentStep.courseId}`, { state: { from: "roadmap" } }); }}
                >
                  Continue course →
                </button>
              )}
            </div>

            {(streak?.freezeCredits ?? 0) > 0 && (
              <div className="freeze-badge">❄️ {streak.freezeCredits} freeze</div>
            )}

            <button
              className="info-btn streak-info-btn"
              onClick={(e) => { e.stopPropagation(); setShowStreakInfo(true); }}
            >
              i
            </button>
          </div>

          {/* 📚 Courses + Roadmaps Card */}
          <div className="stat-card">
            <div className="stat-block">
              <div className="stat-title">Courses</div>
              <div className="stat-value">{courses.length}</div>
              <div className="stat-sub">active</div>
            </div>
            <div className="stat-divider" />
            <div className="stat-block">
              <div className="stat-title">Roadmaps</div>
              <div className="stat-value">{roadmapCount}</div>
            </div>
            <button className="stat-btn" onClick={() => navigate("/roadmaps")}>
              View Roadmaps →
            </button>
          </div>

          {/* 🎯 Daily Target Widget — NEW */}
          <DailyTargetWidget todayMinutes={todayMins} />

          {/* Resume Card */}
          <div className="resume-card">
            <h4>Resume Learning</h4>
            {resume ? (
              <>
                <div className="resume-content">
                  {resume.thumbnail && (
                    <img
                      src={resume.thumbnail}
                      alt={resume.courseTitle ?? "Course thumbnail"}
                      className="resume-thumbnail"
                      onError={(e) => { e.target.style.display = "none"; }}
                    />
                  )}
                  <div className="resume-info">
                    <h3>{resume.courseTitle ?? "Untitled Course"}</h3>
                    {resume.videoTitle && <p>{resume.videoTitle}</p>}
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{ width: `${resume.progressPercent ?? 0}%` }}
                      />
                    </div>
                    <span className="progress-text">{resume.progressPercent ?? 0}% completed</span>
                  </div>
                </div>
                <p className="resume-streak-note">
                  🔥 Watching videos contributes to your daily streak.
                </p>
                {resume.courseId && (
                  <button onClick={() => navigate(`/course/${resume.courseId}`)}>Continue</button>
                )}
              </>
            ) : (
              <>
                <p>No recent activity yet.</p>
                <button onClick={() => navigate("/add-course")}>Add Course</button>
              </>
            )}
          </div>
        </div>

        {/* ── Roadmap Progress Block ───────────────────────────── */}
        {roadmap && !roadmapComplete && roadmapProgress && (
          <div className="roadmap-progress-block">
            <div className="rpb-header">
              <div>
                <h2 className="rpb-title">{roadmap.title ?? "My Roadmap"}</h2>
                <span className="rpb-sub">
                  {roadmapProgress.completionPercent ?? 0}% complete ·{" "}
                  {roadmapProgress.completed ?? 0} of {roadmapProgress.total ?? 0} steps done
                </span>
              </div>
              {roadmap._id && (
                <button className="rpb-view-btn" onClick={() => navigate(`/roadmap/${roadmap._id}`)}>
                  View Roadmap →
                </button>
              )}
            </div>

            <div className="rpb-bar-track">
              <div
                className="rpb-bar-fill"
                style={{ width: `${roadmapProgress.completionPercent ?? 0}%` }}
              />
            </div>

            <div className="rpb-stats">
              <div className="rpb-stat">
                <span className="rpb-stat-val">{roadmapProgress.completedSkills ?? 0}</span>
                <span className="rpb-stat-label">/ {roadmapProgress.totalSkills ?? 0} Skills</span>
              </div>
              <div className="rpb-stat-divider" />
              <div className="rpb-stat">
                <span className="rpb-stat-val">{roadmapProgress.completedProjects ?? 0}</span>
                <span className="rpb-stat-label">/ {roadmapProgress.totalProjects ?? 0} Projects</span>
              </div>
            </div>

            {currentStep && (
              <div className="rpb-current">
                <span className="rpb-current-label">
                  Now — Step {currentStep.stepNumber ?? "?"} of {currentStep.totalSteps ?? "?"}
                </span>

                {currentStep.type === "course" && (
                  <div className="rpb-step-card rpb-step-card--course">
                    {currentStep.thumbnail && (
                      <img
                        src={currentStep.thumbnail}
                        alt=""
                        className="rpb-step-thumb"
                        onError={(e) => { e.target.style.display = "none"; }}
                      />
                    )}
                    <div className="rpb-step-info">
                      {currentStep.skillName && (
                        <span className="rpb-step-context">🎯 {currentStep.skillName}</span>
                      )}
                      <span className="rpb-step-title">{currentStep.title ?? "Untitled"}</span>
                    </div>
                    <button className="rpb-continue-btn" onClick={handleContinueRoadmap}>
                      Continue →
                    </button>
                  </div>
                )}

                {currentStep.type === "project" && (
                  <div className="rpb-step-card rpb-step-card--project">
                    <div className="rpb-step-info">
                      <span className="rpb-step-context">🚀 Project</span>
                      <span className="rpb-step-title">{currentStep.title ?? "Untitled"}</span>
                      {Array.isArray(currentStep.techStack) && currentStep.techStack.length > 0 && (
                        <div className="rpb-tech-row">
                          {currentStep.techStack.slice(0, 3).map((t) => (
                            <span key={t} className="rpb-tech-chip">{t}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <button
                      className="rpb-continue-btn rpb-continue-btn--project"
                      onClick={handleContinueRoadmap}
                    >
                      Go to Project →
                    </button>
                  </div>
                )}
              </div>
            )}

            {upcomingItems.length > 0 && (
              <div className="rpb-upcoming">
                <span className="rpb-upcoming-label">Up next</span>
                <div className="rpb-upcoming-chips">
                  {upcomingItems.map((item, i) => (
                    <span key={i} className={`rpb-upcoming-chip rpb-upcoming-chip--${item?.type ?? "skill"}`}>
                      {item?.type === "skill" ? "🎯" : "🚀"} {item?.name ?? "..."}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Learning Tracks — NEW ─────────────────────────────── */}
        <TrackProgressCard />

        {/* ── Section Two (Productivity + Todo) ───────────────── */}
        <div className="section-two">
          <ProductivityCard
            onProductivitySuccess={() => {
              fetchStreak();
              fetchProductivityStatus();
            }}
          />
          <TodoCard />
        </div>

        {/* ── Analytics ────────────────────────────────────────── */}
        <h2 className="section-title">Your Analytics</h2>
        <p className="chart-insight">Show up daily — even 10 mins counts</p>
        <div className="analytics-grid">
          <div className="chart-card">
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="chart-dropdown"
            >
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
            <FocusChart data={chartData} />
          </div>
          <HeatmapCard
            data={heatmapData}
            score={consistencyScore}
            onDaysChange={setHeatmapDays}
          />
        </div>

        {/* ── Pro Analytics — NEW ──────────────────────────────── */}
        <ProAnalyticsSection />

        {/* ── Roadmap Card ─────────────────────────────────────── */}
        <h2 className="section-title">Your Roadmap</h2>
        <RoadmapCard roadmap={roadmap} onContinue={handleContinueRoadmap} />

        {/* ── My Courses ───────────────────────────────────────── */}
        <div className="section-header">
          <h2>My Courses</h2>
          <button className="add-btn" onClick={() => navigate("/add-course")}>+ Add</button>
        </div>

        <div className="course-grid">
          {courses.map((enrollment) => {
            const course = enrollment?.course;
            if (!course?._id) return null;

            return (
              <div key={enrollment._id ?? course._id} className="course-card">
                <div className="course-thumbnail-wrapper">
                  <img
                    src={course.thumbnail || "https://i.ytimg.com/vi/default/maxresdefault.jpg"}
                    alt={course.title ?? "Course"}
                    className="course-thumbnail"
                    onError={(e) => {
                      e.target.src = "https://i.ytimg.com/vi/default/maxresdefault.jpg";
                    }}
                  />
                </div>
                <div className="course-card-body">
                  <h3>{course.title ?? "Untitled Course"}</h3>
                  <p>{course.totalVideos ?? 0} videos</p>
                  <div className="course-card-btns">
                    <button
                      className="delete-course-btn"
                      onClick={() => { setCourseToDelete(course); setShowDeleteCourseModal(true); }}
                    >
                      🗑
                    </button>
                    <button onClick={() => navigate(`/course/${course._id}`)}>Open</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* <div className="upcoming-section">
          <div className="upcoming-overlay">
            <div className="lock-icon">🔒</div>
            <h3>Upcoming Features</h3>
            <p>Free Online Resources Management, Deep Learning analytics, detailed insights, and more.</p>
            <span>Coming Soon</span>
          </div>
        </div> */}

      </div>

      <ProductTour run={runTour} setRun={setRunTour} />

      {/* ── Modals ───────────────────────────────────────────── */}
      {showStreakModal && (
        <StreakSettingsModal onClose={() => setShowStreakModal(false)} />
      )}

      {showStreakInfo && (
        <InfoModal
          title="Streak System Explained"
          text="Your streak increases when you complete at least one lesson per day. If you miss a day, your streak resets unless streak protection is enabled."
          onClose={() => setShowStreakInfo(false)}
        />
      )}

      {showProductivityModal && (
        <ProductivityModal
          onClose={() => setShowProductivityModal(false)}
          onSuccess={() => {
            fetchStreak();
            fetchProductivityStatus();
            setShowProductivityModal(false);
          }}
        />
      )}

      {showDeleteCourseModal && courseToDelete && (
        <>
          <div className="modal-backdrop" />
          <div className="delete-confirm-modal">
            <h3>Delete Course</h3>
            <p>
              You are about to remove:<br />
              <strong>{courseToDelete.title ?? "this course"}</strong><br /><br />
              This will remove your progress from this course.<br />
              Type <strong>DELETE</strong> to confirm.
            </p>
            <input
              type="text"
              placeholder="Type DELETE"
              value={deleteText}
              onChange={(e) => setDeleteText(e.target.value)}
            />
            <div className="delete-actions">
              <button
                disabled={deleteText !== "DELETE" || deleteLoading}
                className="delete-btn"
                onClick={handleDeleteCourse}
              >
                {deleteLoading ? "Deleting…" : "Confirm Delete"}
              </button>
              <button
                className="cancel-btn"
                onClick={() => {
                  setShowDeleteCourseModal(false);
                  setDeleteText("");
                  setCourseToDelete(null);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </>
      )}

      {showStreakFreezeModal && (
        <StreakFreezeModal
          freezeCredits={freezeCredits}
          onClose={() => setShowStreakFreezeModal(false)}
          onResolved={() => { setMissedDay(false); fetchStreak(); }}
        />
      )}
    </div>
  );
}

export default Dashboard;